// Default 5-namespace `Resolvers` generator for Channel 1.
//
// `__bind` is real — derived from the example's `bindingSchema.properties.__bind` shape (which
// declares which keys/maxLength constraints exist). The 4 other namespaces are stubs returning
// `undefined`, matching the dev playground policy: 229-corpus templates use `?? 'default'` literal
// fallbacks for `__i18n`, no `@ds:` token expressions today, and `__symbol` / `__app` are
// conventionally undefined in dev.
//
// `Resolvers` interface (`packages/cogo-binder/src/types.ts`) declares all 5 namespaces as
// REQUIRED, so the binder type-check forces us to provide every slot — stubs let unresolved tokens
// degrade to non-fatal warnings instead of throwing.
//
// Plan: docs/superpowers/plans/2026-04-28-phase-a-sub-plan-a3-1-channel-1-dispatcher.md (Task 4).

import type { Resolvers, BindResolver } from '@cogo/binder'

interface BindKeyShape {
  maxLength?: number
  minLength?: number
}

/**
 * Read the `__bind.properties` block of an example's bindingSchema and project each key's
 * `value.maxLength` / `value.minLength` constraints into a flat lookup map.
 *
 * Returns `{}` when bindingSchema is undefined or lacks `__bind` — caller treats unknown keys as
 * `undefined`.
 */
function readBindKeys(
  bindingSchema: Record<string, unknown> | undefined,
): Record<string, BindKeyShape> {
  if (!bindingSchema) return {}
  const props = (bindingSchema as { properties?: Record<string, unknown> }).properties
  const bind = props?.__bind as Record<string, unknown> | undefined
  const bindProps = (bind?.properties ?? {}) as Record<string, unknown>
  const out: Record<string, BindKeyShape> = {}
  for (const [key, shape] of Object.entries(bindProps)) {
    const value = (shape as { properties?: Record<string, unknown> }).properties?.value as
      | Record<string, unknown>
      | undefined
    out[key] = {
      maxLength: typeof value?.maxLength === 'number' ? value.maxLength : undefined,
      minLength: typeof value?.minLength === 'number' ? value.minLength : undefined,
    }
  }
  return out
}

/**
 * Pick a placeholder string for a `__bind.<key>.value` slot, keyed off the key's role prefix.
 *
 * Order in the chain matters: `text_0` / `text_1` are checked BEFORE the generic `text_` so they
 * get distinctive heading/subtitle samples instead of the generic `Sample text 0` form.
 */
function placeholderFor(key: string, shape: BindKeyShape): string {
  const base =
    key.startsWith('checkbox_') ? 'Remember me' :
    key.startsWith('button_')   ? 'Click me' :
    key.startsWith('label_')    ? 'Label' :
    key.startsWith('text_0')    ? 'Sample heading' :
    key.startsWith('text_1')    ? 'Sample subtitle' :
    key.startsWith('text_')     ? `Sample text ${key.slice(5)}` :
    key.startsWith('placeholder_') ? 'Type here…' :
    `Sample ${key}`

  // maxLength truncate (preserve ≥ minLength=1 — all defaults are non-empty so single-char
  // truncation still passes minLength=1).
  if (shape.maxLength && base.length > shape.maxLength) {
    return base.slice(0, shape.maxLength)
  }
  return base
}

/**
 * Build a real `BindResolver` from the schema-derived key map.
 *
 * Three-tier resolution order:
 *  1. Caller-supplied `override` (e.g. user-typed values during refinement loop) — consulted first.
 *  2. `blockDefaults` — literals extracted from the example's `blockJson` at the same tree
 *     position as each `#{__bind.<key>.value}` expression. Provides realistic copy for first-shot
 *     dispatch without resolverOverrides (Issue #8). Returned as-is, no maxLength truncation.
 *  3. Schema-driven generic placeholder ("Sample heading", "Sample text 0" etc.) — final fallback.
 *
 * Path shape: templates reference `#{__bind.text_0.value}` so the binder calls
 * `resolve('text_0.value')`. We split on the LAST `.`, look up the key in `bindKeys`, and ignore
 * any tail other than `'value'` (the only sub-property declared in 229-corpus schemas today).
 */
function makeBindResolver(
  bindKeys: Record<string, BindKeyShape>,
  override?: BindResolver,
  blockDefaults?: Record<string, string>,
): BindResolver {
  return {
    resolve(path: string): unknown {
      // Tier 1: override first — when override returns undefined we fall through.
      if (override) {
        const v = override.resolve(path)
        if (v !== undefined) return v
      }
      // Tier 2: blockJson-derived default — realistic corpus literal at the bind position.
      if (blockDefaults) {
        const d = blockDefaults[path]
        if (d !== undefined) return d
      }
      // Tier 3: schema-driven generic placeholder.
      // Path shape: "<key>.value" — strip ".value" suffix.
      const dot = path.lastIndexOf('.')
      const key = dot === -1 ? path : path.slice(0, dot)
      const tail = dot === -1 ? '' : path.slice(dot + 1)
      const shape = bindKeys[key]
      if (!shape) return undefined
      // Only `.value` and bare key (no tail) are supported — corpus has no nested `__bind` shapes.
      if (tail !== 'value' && tail !== '') return undefined
      return placeholderFor(key, shape)
    },
  }
}

/**
 * Build a default `Resolvers` bundle suitable for Channel 1 dispatch.
 *
 * @param bindingSchema   The example's `bindingSchema` (JSON Schema). Drives `__bind` defaults.
 * @param overrides       Optional per-namespace overrides. When `overrides.__bind` is present it's
 *                        threaded through `makeBindResolver` (consulted first, with schema fallback);
 *                        the other 4 namespaces are replaced wholesale.
 * @param blockDefaults   Optional map of `__bind` path → literal extracted from the example's
 *                        `blockJson`. Typically produced by `extractBindDefaults(template, blockJson)`.
 *                        Used as the 2nd resolution tier (after caller override, before schema
 *                        generic placeholder). Enables first-shot dispatch to show realistic copy
 *                        instead of "Sample heading" / "Sample text 0" (Issue #8).
 *
 * Resolution priority inside `__bind.resolve(path)`:
 *  1. `overrides.__bind.resolve(path)` — caller / refinement-loop user input
 *  2. `blockDefaults[path]`            — blockJson-derived corpus literal (NEW, Issue #8)
 *  3. `placeholderFor(key, shape)`     — schema-driven generic placeholder
 *
 * Stubs:
 *  - `__i18n.resolve`        → `undefined` (templates rely on `?? 'default'` fallbacks)
 *  - `__ds.resolveTokenName` → `undefined` (no `@ds:` token expansion in 229-corpus today)
 *  - `__ds.resolveTokenPath` → `undefined`
 *  - `__symbol.resolve`      → `undefined` (Phase 2c stub matches the binder's own contract)
 *  - `__app.passThroughPrefix` → `'__app'` (binder leaves `#{__app.X}` verbatim for runtime eval)
 */
export function defaultResolvers(
  bindingSchema: Record<string, unknown> | undefined,
  overrides?: Partial<Resolvers>,
  blockDefaults?: Record<string, string>,
): Resolvers {
  const bindKeys = readBindKeys(bindingSchema)
  return {
    __bind: makeBindResolver(bindKeys, overrides?.__bind, blockDefaults),
    __i18n: overrides?.__i18n ?? { resolve: () => undefined },
    __ds: overrides?.__ds ?? {
      resolveTokenName: () => undefined,
      resolveTokenPath: () => undefined,
    },
    __symbol: overrides?.__symbol ?? { resolve: () => undefined },
    __app: overrides?.__app ?? { passThroughPrefix: '__app' },
  }
}
